import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import InputField from 'components/common/InputField'
import PrimaryButton from 'components/common/PrimaryButton'
import ZeroneLoader from 'components/common/ZeroneLoader'
import AnimatedBackgroundContainer from 'components/common/AnimatedBackgroundContainer'
import { useToast } from 'provider/ToastProvider'
import { changePassword } from 'constants/api/endpoints/AuthEndpoints'
import { useAuth } from 'hooks/AuthContext'
import styles from 'styles/components/LoginForm.module.sass'

/**
 * Forced first-login password change. Reached only right after a successful
 * login where the server reported mustChangePassword=true (accounts created
 * with a system-generated password, e.g. Pilot admin-created accounts) -
 * never something the user chose to navigate to. The session JWT from that
 * login is already stored, so the change-password request authenticates
 * automatically the same way any other protected API call does.
 */
const ChangePasswordRequiredPage: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { addToast } = useToast()
    const { refreshUser } = useAuth()
    const { redirect } = (location.state as { redirect?: string } | null) ?? {}

    const [newPassword, setNewPassword] = useState('')
    const [confirmPw, setConfirmPw]     = useState('')
    const [loading, setLoading]         = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (newPassword !== confirmPw) {
            addToast({ message: 'Passwords do not match.', type: 'error' })
            return
        }

        setLoading(true)
        const result = await changePassword({ tag: 'ChangePasswordRequired', data: { newPassword, confirmPassword: confirmPw } })
        setLoading(false)

        if (result.error) {
            addToast({ message: result.error, type: 'error' })
            return
        }

        addToast({ message: 'Password updated!', type: 'success' })
        // Refresh AuthContext's cached user BEFORE navigating away - the new
        // global RequireChangePasswordGate reads user.mustChangePassword on
        // every route, so a stale (still-true) value would immediately
        // bounce this navigation right back here.
        await refreshUser()
        navigate('/success', {
            state: {
                kind: 'password-changed',
                title: 'You\'re all set!',
                message: 'Your password has been updated. Welcome to ZeroneAnalytiQs.',
                redirectTo: redirect ?? '/',
                messageType: 'success',
            }
        })
    }

    return (
        <AnimatedBackgroundContainer showFooter={true}>
            <form className={styles['login-form']} onSubmit={(e) => { void handleSubmit(e) }}>
                <div className={styles['logo-wrapper']}>
                    <Link to="/"><img src={zeroneLogoDark} className={styles['logo']} alt="Zerone AnalytiQs Logo" /></Link>
                </div>
                {loading ? (
                    <ZeroneLoader text="Updating your password..." />
                ) : (
                    <div className={styles['main-wrapper']}>
                        <div className={styles['input-wrapper']}>
                            <p style={{ fontSize: 14, color: '#374151', margin: '0 0 8px', textAlign: 'center' }}>
                                Welcome! For your security, please set your own password before continuing.
                            </p>
                            <InputField label="New Password" type="password" value={newPassword} onChange={setNewPassword} required />
                            <InputField label="Confirm Password" type="password" value={confirmPw} onChange={setConfirmPw} required />
                        </div>
                        <div className={styles['action-buttons']}>
                            <PrimaryButton
                                text="Set Password"
                                width="auto"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={(e: React.FormEvent) => { void handleSubmit(e) }}
                            />
                        </div>
                    </div>
                )}
            </form>
        </AnimatedBackgroundContainer>
    )
}

export default ChangePasswordRequiredPage
